import { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Divider } from '@mui/material'
import { ActivityDayDialog } from '@/components/activity/ActivityDayDialog'
import { ActivityTimeline } from '@/components/activity/ActivityTimeline'
import { HeatmapGrid } from './HeatmapGrid'
import type { HeatmapPoint } from '@/types/dashboard'

interface ActivityWidgetContentProps {
  from: string
  to: string
  days: HeatmapPoint[]
  /** Высота виджета в рядах: 1 — только график, 2+ — график и таймлайн */
  rows: number
  /** Сводная лента учителя по ученикам */
  scope?: 'students'
  /** Показывать имя автора события (учитель) */
  showUserName?: boolean
  /** Дополнительный контент над графиком (например, счётчик серии) */
  header?: React.ReactNode
}

/**
 * Наполнение виджетов активности с учётом высоты блока.
 *
 * Клетки фиксированного размера (не растягиваются по ширине): иначе на широком
 * виджете высота графика превышала бы ряд сетки и появлялся бы вертикальный
 * скролл. Таймлайн монтируется и делает запрос только при высоте 2+ рядов.
 */
export function ActivityWidgetContent({
  from,
  to,
  days,
  rows,
  scope,
  showUserName = false,
  header,
}: ActivityWidgetContentProps) {
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  const withTimeline = rows >= 2

  // Сигнатура данных heatmap: меняется после refetch виджета по сокету
  const signature = useMemo(
    () => `${days.length}:${days.reduce((total, point) => total + point.count, 0)}`,
    [days],
  )

  // Эффекты потомка выполняются раньше родительских, поэтому на первом
  // монтировании бампить ключ нельзя — лента загрузилась бы дважды
  const knownSignature = useRef(signature)

  useEffect(() => {
    if (knownSignature.current === signature) return
    knownSignature.current = signature
    setReloadKey((key) => key + 1)
  }, [signature])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        // Одноряд­ный виджет не должен прокручиваться по вертикали
        overflow: withTimeline ? 'visible' : 'hidden',
      }}
    >
      {header}

      <Box sx={{ flexShrink: 0 }}>
        <HeatmapGrid from={from} to={to} days={days} compact onDayClick={setSelectedDate} />
      </Box>

      {withTimeline && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto' }}>
            <ActivityTimeline
              scope={scope}
              showUserName={showUserName}
              perPage={10}
              reloadKey={reloadKey}
            />
          </Box>
        </>
      )}

      <ActivityDayDialog
        open={selectedDate !== null}
        date={selectedDate}
        onClose={() => setSelectedDate(null)}
        scope={scope}
        showUserName={showUserName}
      />
    </Box>
  )
}
